<div class="fullcontainer pr-3">
  <div class="sheet">
    <div class="titleContainer">
      <h1 class="header">Report Builder - Aggregate</h1>
      <div>
        <button class="btn btn-secondary" routerLink="/report-builder">All Report Builders</button>
      </div>
    </div>

    <p class="text-muted">
      Group and total data across the system - orders, accounts, jobs, invoices, credit limits and
      stock movements. Pick an entity, optionally join related records, then either list rows or
      group and aggregate them.
    </p>

    @if (this.loadFailed) {
      <div class="alert alert-danger mb-0"><strong>The report builder schema could not be loaded.</strong></div>
    }

    <div class="row">
      <div class="col-md-4">
        <label>Entity</label>
        <select class="form-control" (change)="this.onEntityChanged($event.target.value)">
          <option value="">Choose an entity&hellip;</option>
          @for (item of this.schemas; track item.key) {
            <option [value]="item.key">{{ item.label }}</option>
          }
        </select>
      </div>
    </div>

    @if (this.schema) {
      <br>

      @if (this.savedReports.length > 0) {
        <div class="row">
          <div class="col-md-6">
            <label>Saved reports for {{ this.schema.label }}</label>
            <div class="input-group">
              <select class="form-control" [ngModel]="this.selectedSavedReportId"
                (ngModelChange)="this.loadSavedReport($event)">
                <option [ngValue]="-1">Choose a saved report&hellip;</option>
                @for (report of this.savedReports; track report.id) {
                  <option [ngValue]="report.id">{{ report.name }}</option>
                }
              </select>
            </div>
          </div>
        </div>
        <br>
      }

      <div class="row">
        <div class="col-md-6">
          <label>Report name</label>
          <input type="text" class="form-control" [(ngModel)]="this.reportName" placeholder="Name this report to save it">
        </div>
        <div class="col-md-6 d-flex align-items-end">
          <button class="btn btn-secondary mr-2" (click)="this.saveReport()" [disabled]="this.saving || !this.reportName">
            {{ this.currentSavedReport ? 'Update Saved Report' : 'Save Report' }}
          </button>
          @if (this.currentSavedReport) {
            <button class="btn btn-secondary mr-2" (click)="this.newReport()">New</button>
            <button class="btn btn-danger" (click)="this.deleteSavedReport()">Delete</button>
          }
        </div>
      </div>

      @if (this.saveFailed) {
        <div class="alert alert-danger mb-0 mt-2">The report could not be saved.</div>
      }

      <br>

      @if (this.schema.joins.length > 0) {
        <label>Join</label>
        <div class="report-builder-picker">
          @for (join of this.schema.joins; track join.key) {
            <div class="form-check">
              <input type="checkbox" class="form-check-input" [id]="'join-' + join.key"
                [checked]="this.selectedJoins[join.key]" (change)="this.toggleJoin(join.key)">
              <label class="form-check-label" [for]="'join-' + join.key">{{ join.label }}</label>
            </div>
          }
        </div>
        <br>
      }

      <div class="form-check form-check-inline">
        <input type="radio" class="form-check-input" id="mode-rows" name="mode" [checked]="!this.isAggregate" (change)="this.isAggregate = false">
        <label class="form-check-label" for="mode-rows">List rows</label>
      </div>
      <div class="form-check form-check-inline">
        <input type="radio" class="form-check-input" id="mode-aggregate" name="mode" [checked]="this.isAggregate" (change)="this.isAggregate = true">
        <label class="form-check-label" for="mode-aggregate">Group &amp; aggregate</label>
      </div>

      <br><br>

      @if (!this.isAggregate) {
        <label>Columns</label>
        <div class="report-builder-picker">
          @for (column of this.availableColumns; track column.key) {
            <div class="form-check">
              <input type="checkbox" class="form-check-input" [id]="'col-' + column.key"
                [checked]="this.selectedColumns[column.key]" (change)="this.toggleColumn(column.key)">
              <label class="form-check-label" [for]="'col-' + column.key">{{ column.label }}</label>
            </div>
          }
        </div>
      }

      @if (this.isAggregate) {
        <div class="row">
          <div class="col-md-6">
            <label>Group by</label>
            <div class="report-builder-picker">
              @for (column of this.groupableColumns(); track column.key) {
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" [id]="'group-' + column.key"
                    [checked]="this.selectedGroupBy[column.key]" (change)="this.toggleGroupBy(column.key)">
                  <label class="form-check-label" [for]="'group-' + column.key">{{ column.label }}</label>
                </div>
              }
              @if (this.groupableColumns().length === 0) {
                <p class="text-muted m-0">No groupable columns for this entity/joins.</p>
              }
            </div>
          </div>

          <div class="col-md-6">
            <label>Aggregates</label>
            @for (row of this.aggregateRows; track $index) {
              <div class="row mb-2">
                <div class="col-6">
                  <select class="form-control" [(ngModel)]="row.column">
                    @for (column of this.schema.aggregates; track column.key) {
                      <option [value]="column.key">{{ column.label }}</option>
                    }
                  </select>
                </div>
                <div class="col-4">
                  <select class="form-control" [(ngModel)]="row.fn">
                    @for (fn of this.functionsFor(row.column); track fn) {
                      <option [value]="fn">{{ fn }}</option>
                    }
                  </select>
                </div>
                <div class="col-2">
                  <button class="btn btn-danger" (click)="this.removeAggregateRow($index)">&times;</button>
                </div>
              </div>
            }
            <button class="btn btn-secondary" (click)="this.addAggregateRow()" [disabled]="!this.schema.aggregates.length">
              + Add aggregate
            </button>
          </div>
        </div>
      }

      <br>
      <label>Filters</label>
      @for (filter of this.filters; track $index) {
        <div class="row mb-2">
          <div class="col-4">
            <select class="form-control" [(ngModel)]="filter.column" (change)="this.onFilterColumnChanged(filter)">
              @for (column of this.availableColumns; track column.key) {
                <option [value]="column.key">{{ column.label }}</option>
              }
            </select>
          </div>
          <div class="col-3">
            <select class="form-control" [(ngModel)]="filter.operator">
              @for (operator of this.operatorsFor(filter.column); track operator.value) {
                <option [value]="operator.value">{{ operator.label }}</option>
              }
            </select>
          </div>
          <div class="col-3">
            @if (this.needsValue(filter.operator)) {
              <input type="text" class="form-control" [(ngModel)]="filter.value">
            }
          </div>
          <div class="col-2">
            <button class="btn btn-danger" (click)="this.removeFilter($index)">&times;</button>
          </div>
        </div>
      }
      <button class="btn btn-secondary" (click)="this.addFilter()" [disabled]="!this.availableColumns.length">+ Add filter</button>

      <br><br>
      <div class="row">
        <div class="col-md-3">
          <label>Sort by</label>
          <select class="form-control" [(ngModel)]="this.sort">
            <option value="">Default</option>
            @for (column of this.sortableColumns(); track column.key) {
              <option [value]="column.key">{{ column.label }}</option>
            }
          </select>
        </div>
        <div class="col-md-3">
          <label>Direction</label>
          <select class="form-control" [(ngModel)]="this.direction">
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
        <div class="col-md-3">
          <label>Row limit</label>
          <input type="number" class="form-control" min="1" max="20000" [(ngModel)]="this.limit">
        </div>
      </div>

      <br>
      <button class="btn btn-primary" (click)="this.runReport()" [disabled]="this.running">Run Report</button>
    }
  </div>

  <div class="sheet">
    @if (this.running) {
      <p class="m-0"><i class="fas fa-spinner fa-spin"></i> Running&hellip;</p>
    }

    @if (this.runFailed) {
      <div class="alert alert-danger mb-0">
        <strong>The report could not be run.</strong> {{ this.runError }}
      </div>
    }

    @if (this.hasRun && !this.running && !this.runFailed) {
      <div class="titleContainer">
        <h1 class="header">Results ({{ this.result.rows.length }})</h1>
        <button class="btn btn-secondary" (click)="this.exportToCSV()" [disabled]="this.result.rows.length === 0">Export to CSV</button>
      </div>

      @if (this.result.rows.length === 0) {
        <div class="alert alert-success">No rows matched.</div>
      }

      @if (this.result.rows.length > 0) {
        <table class="table table-bordered table-hover">
          <thead>
            <tr>
              @for (column of this.resultColumns; track column.field) {
                <th>{{ column.label }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of this.result.rows; track $index) {
              <tr>
                @for (column of this.resultColumns; track column.field) {
                  <td>{{ this.getCell(row, column) }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      }
    }
  </div>
</div>
